// growth-timeline
import { onDestroy, onMount, onView } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { Raf, Resize } from "@lib/subs";
import { clamp } from "@utils/math";

const TOP_PAD = 12;
const EDGE_PAD = 28;
const RISE_START = "2025-01-01"; // rest shape stays flat until here, then hockey-sticks up
const RISE_EXP = 2.6; // steepness of the post-2025 climb
const REST_TOP_FRAC = 0.62; // tallest resting bar as a fraction of usable plot height
const FLUFF_BOOST = 24;
const FALLOFF = 70;
const REVEAL_RADIUS = 46;
const FOCUS_LERP = 0.25;
const WAVE_RISE = 0.2;
const WAVE_FALL = 0.1;
const PILL_IN = 0.26;
const PILL_OUT = 0.15;
const PILL_RISE = 8;
const PILL_EASE_IN = "power3.out";
const PILL_EASE_OUT = "power2.in";
const PILL_GAP = 6;
const PILL_Z = 10; // later-DOM bars paint over pills without this
const PILL_LEAD = 16;
const PILL_EDGE = 8;
const YEAR_CLASS = "is--year";
const MILESTONE_CLASS = "is--milestone";
const HIGHLIGHT_CLASS = "is--highlight";
const ACTIVE_COLOR = "#fdbe11";
const MILESTONE_THICK = 1.6;
const COLOR_FADE = 0.25;
const DEFAULT_BAR_COLOR = "#1b1a18";
// magnitude → bar height as a fraction of the usable plot height
const MAGNITUDE: Record<string, number> = {
  small: 0.5,
  medium: 0.68,
  large: 0.85,
  peak: 1,
};

const INTRO_SWEEP = true;
const INTRO_DELAY = 0.35;
const INTRO_DURATION = 2.4;
const INTRO_RAMP = 0.16; // 0..0.5 — smooth swell-in / fade-out fraction at each end of the sweep
const INTRO_EASE = "sine.inOut";
const OFFSCREEN_MARGIN = "200px";

const TAP_LOCK = true;
const LOCK_RADIUS = 64;
const AUTO_LOOP = true;
const AUTO_LOOP_GAP = 1.2;

const MAGNETISM = true;
const MAGNET_RADIUS = 54;
const MAGNET_PULL = 0.35;
const VELOCITY = true;
const VEL_REF = 30;
const VEL_MAX = 1.8;
const VEL_SMOOTH = 0.15;

const PEEK_AT_REST = true;
const PEEK_FRACTION = 0.12;
const PHASE_EMPHASIS = true;
const PHASE_ACTIVE_CLASS = "is--active";
const PHASE_DIM = 0.4;
const PHASE_FADE = 0.3;
const LAUNCH_COLOR = true;
const PRE_LAUNCH_COLOR = "#1b1a18"; // neutral before Official Launch
const POST_LAUNCH_COLOR = "#3b9fd8"; // blue after Official Launch (swap for brand blue)
const PEAK_COLOR = ACTIVE_COLOR; // $100M peak stays highlighted at rest

const CULL_SIGMAS = 3.5;
const WILL_CHANGE = true;
const A11Y_LABEL = "Company growth timeline";
const A11Y_HINT =
  "Hover or focus a milestone to reveal it. Use arrow keys to move between milestones.";

const TOUCH_DRAG = true;
const MOBILE_BREAKPOINT = 767; // px — at/below this the comb is drag-mode even with a mouse
const MOBILE_YEAR_PX = 95;
const MARKER_FRAC = 0.28;
const DRAG_THRESH = 6;
const TAP_MAX = 8;
const STALE_MS = 70; // pausing longer than this before lifting → velocity ignored (no fling)
const OVERSCROLL = 0.35;
const MS_MAGNET = 44;
const INERTIA_MS = 220;
const INERTIA_SPEED = 1500;
const INERTIA_MIN = 0.3;
const INERTIA_MAX = 1.3;
const HAPTIC_MS = 11; // navigator.vibrate length per tick (Android; iOS no-op)
const HAPTIC_GAP = 45;
const SPEED_FULL = 0.8;
const STRENGTH_EASE = 0.25;
const REVEAL_STRENGTH = 0.5;
const BLOOM_DUR = 0.4;
const BLOOM_DELAY = 0.08;
const SNAP_EASE = "back.out(2.2)";
const SNAP_DUR = 0.42;
const DETENT_HAPTIC = 7;
// clip technique: visually hidden but readable by screen readers
const SR_ONLY: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: "0",
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: "0",
};

type Milestone = {
  time: number;
  dateLabel: string;
  magnitude: string;
  label: string;
  logoSrc: string | null;
  highlight: boolean;
};

type Tick = {
  time: number;
  frac: number;
  isYear: boolean;
  el: HTMLElement;
  bar: HTMLElement;
  x: number;
  restPx: number;
  activePx: number;
  milestone: Milestone | null;
  pill: HTMLElement | null;
  baseColor: string;
  dirty: boolean;
};

export default function (element: HTMLElement) {
  const stage = element.querySelector<HTMLElement>('[growth-timeline="stage"]');
  const template = element.querySelector<HTMLElement>(
    '[growth-timeline="item"]',
  );
  const milestoneEls = Array.from(
    element.querySelectorAll<HTMLElement>('[growth-timeline="milestone"]'),
  );

  if (!stage || !template || milestoneEls.length === 0) return;

  const barSeed = template.querySelector<HTMLElement>(".about_timeline_bars");
  const baseBarPx = barSeed
    ? parseFloat(getComputedStyle(barSeed).height) || 10
    : 10;

  let ticks: Tick[] = [];
  let phases: {
    el: HTMLElement;
    lineW: HTMLElement | null;
    start: number;
    end: number;
    x0: number;
    x1: number;
  }[] = [];
  let stageH = 220;

  // `strength` is separate from `focus` so the wave fades in place on disengage instead of sliding off-screen
  let targetFocus = 0;
  let focus = 0;
  let strength = 0;
  let engaged = false;
  let idle = true;
  let activeIndex = -1;

  let inView = false;
  let intro = false;
  let introTween: gsap.core.Tween | null = null;
  let introPlayed = false;

  let lockedIndex = -1;

  // Mode is re-evaluated on resize / pointer change (see relayout), not latched.
  const fineQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
  const prospectiveDrag = () =>
    TOUCH_DRAG &&
    (!fineQuery.matches || window.innerWidth <= MOBILE_BREAKPOINT);
  let hasFinePointer = fineQuery.matches;
  let dragMode = prospectiveDrag();
  let stopRaf = () => {};
  let noDragTarget: HTMLElement | null = null;

  function computeMode() {
    hasFinePointer = fineQuery.matches;
    dragMode = prospectiveDrag();
  }

  let dragging = false;
  let combTrack: HTMLElement | null = null;
  let markerEl: HTMLElement | null = null;
  let setCombX: ((v: number) => void) | null = null;
  let offset = 0;
  let dragStartX = 0;
  let dragStartOffset = 0;
  let yearSpan = 1;
  let velocity = 0;
  let lastMoveX = 0;
  let lastMoveTime = 0;
  let pending = false;
  let pointerId = -1;
  let downX = 0;
  let downY = 0;
  let lastVibrate = 0;
  let lastTickIndex = -1;
  let bloomTween: gsap.core.Tween | null = null;

  let prevTarget = 0;
  let speed = 0;

  let activePhase = -1;
  let launchTime = Infinity; // Official Launch phase start — bars turn blue at/after this

  let hintEl: HTMLElement | null = null;

  // Bare ISO dates ("2017-01-01", "2017-04", "2017") must parse as LOCAL time — the Date constructor treats them as UTC and would slip a day on negative-offset zones.
  function parseDate(raw: string): number {
    const iso = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(raw.trim());
    if (iso) {
      return new Date(
        +iso[1]!,
        iso[2] ? +iso[2] - 1 : 0,
        iso[3] ? +iso[3] : 1,
      ).getTime();
    }
    return new Date(raw).getTime();
  }

  function parseMilestones(): Milestone[] {
    return milestoneEls
      .map((el): Milestone | null => {
        const raw = el.dataset.date ?? "";
        const time = parseDate(raw);
        if (!Number.isFinite(time)) return null;

        // .w-dyn-bind-empty is the Webflow placeholder for unset CMS image fields; exclude it
        const assetImg = el.querySelector<HTMLImageElement>(
          'img[growth-timeline="asset"]:not(.w-dyn-bind-empty)',
        );
        const highlight = !!el.querySelector(
          '[growth-timeline="asset-wrap"] p',
        );

        return {
          time,
          dateLabel: raw,
          magnitude: (el.dataset.magnitude || "small").toLowerCase(),
          label: el.dataset.label || el.textContent?.trim() || "",
          logoSrc: assetImg?.getAttribute("src") || null,
          highlight,
        };
      })
      .filter((m): m is Milestone => m !== null)
      .sort((a, b) => a.time - b.time);
  }

  function build() {
    const model = parseMilestones();
    if (model.length === 0) return;

    const minYear = new Date(model[0]!.time).getFullYear();
    const maxYear = new Date(model[model.length - 1]!.time).getFullYear();
    const domainEnd = new Date(maxYear + 1, 0, 1).getTime();
    yearSpan = maxYear + 1 - minYear;

    template!.remove();

    const raw: Omit<Tick, "frac">[] = [];
    for (let y = minYear, m = 0; ; m++) {
      if (m > 11) {
        m = 0;
        y++;
      }
      const time = new Date(y, m, 1).getTime();
      if (time > domainEnd) break;

      const isYear = m === 0;
      const el = template!.cloneNode(true) as HTMLElement;
      const bar = el.querySelector<HTMLElement>(".about_timeline_bars")!;
      bar.style.transformOrigin = "50% 100%";

      const axis = el.querySelector<HTMLElement>('[growth-timeline="axis"]');
      if (isYear && axis) {
        const p = el.querySelector<HTMLElement>('[growth-timeline="axis-p"]');
        if (p) p.textContent = String(y);
      } else {
        axis?.remove();
      }

      const pill = el.querySelector<HTMLElement>(
        '[growth-timeline="annotations"]',
      );
      stage!.appendChild(el);
      raw.push({
        time,
        isYear,
        el,
        bar,
        x: 0,
        restPx: baseBarPx,
        activePx: baseBarPx,
        milestone: null,
        pill: pill ?? null,
        baseColor: "",
        dirty: false,
      });
    }

    ticks = raw.map((t, i) => ({ ...t, frac: i / (raw.length - 1 || 1) }));

    const indexForTime = (t: number) => {
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < ticks.length; i++) {
        const d = Math.abs(ticks[i]!.time - t);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      return best;
    };

    for (const m of model) {
      let idx = indexForTime(m.time);
      while (ticks[idx]?.milestone && idx < ticks.length - 1) idx++;
      const tick = ticks[idx];
      if (!tick) continue;

      tick.milestone = m;
      tick.bar.classList.add(MILESTONE_CLASS);
      if (m.highlight) tick.bar.classList.add(HIGHLIGHT_CLASS);
      if (WILL_CHANGE) tick.bar.style.willChange = "transform";

      if (tick.pill) {
        const p = tick.pill.querySelector<HTMLElement>(
          '[growth-timeline="annotations-p"]',
        );
        if (p) p.textContent = m.label;
        const logoW = tick.pill.querySelector<HTMLElement>(
          '[growth-timeline="logo-w"]',
        );
        const logo = tick.pill.querySelector<HTMLImageElement>(
          '[growth-timeline="logo"]',
        );
        if (m.logoSrc && logo) logo.src = m.logoSrc;
        else logoW?.remove();
        gsap.set(tick.pill, { autoAlpha: 0, zIndex: PILL_Z });
      }

      tick.el.tabIndex = 0;
      tick.el.setAttribute("role", "button");
      tick.el.setAttribute(
        "aria-label",
        `${m.label}${m.dateLabel ? `, ${m.dateLabel}` : ""}`,
      );
    }

    for (const t of ticks) {
      if (t.milestone) continue;
      t.pill?.remove();
      t.pill = null;
      if (t.isYear) t.bar.classList.add(YEAR_CLASS);
    }

    const parsed = Array.from(
      element.querySelectorAll<HTMLElement>('[growth-timeline="phase"]'),
    )
      .map((el) => ({
        el,
        lineW: el.querySelector<HTMLElement>(
          '[growth-timeline="phase-line-w"]',
        ),
        start: parseDate(el.dataset.start ?? ""),
      }))
      .filter((p) => Number.isFinite(p.start))
      .sort((a, b) => a.start - b.start);

    phases = parsed.map((p, i) => ({
      ...p,
      end: p.el.dataset.end
        ? parseDate(p.el.dataset.end)
        : (parsed[i + 1]?.start ?? domainEnd),
      x0: 0,
      x1: 0,
    }));

    // Anchor the blue era to the Official Launch phase (matched by its label text).
    const launchPhase = phases.find((p) =>
      /launch/i.test(p.el.textContent ?? ""),
    );
    launchTime = launchPhase ? launchPhase.start : Infinity;
  }

  function setBarColor(t: Tick, color: string) {
    if (reduced) {
      gsap.set(t.bar, { backgroundColor: color });
      return;
    }
    gsap.to(t.bar, {
      backgroundColor: color,
      duration: COLOR_FADE,
      overwrite: "auto",
    });
  }

  function updatePhase() {
    if (!PHASE_EMPHASIS || phases.length === 0) return;
    let idx = -1;
    if (engaged || intro || (dragMode && activeIndex >= 0)) {
      for (let i = 0; i < phases.length; i++) {
        const p = phases[i]!;
        if (focus >= p.x0 && focus <= p.x1) {
          idx = i;
          break;
        }
      }
    }
    if (idx === activePhase) return;
    activePhase = idx;
    phases.forEach((p, i) => {
      const on = i === idx;
      p.el.classList.toggle(PHASE_ACTIVE_CLASS, on);
      gsap.to(p.el, {
        opacity: idx < 0 ? 1 : on ? 1 : PHASE_DIM,
        duration: PHASE_FADE,
        overwrite: "auto",
      });
    });
  }

  function xForTime(t: number): number {
    const a = ticks[0];
    const b = ticks[ticks.length - 1];
    if (!a || !b || b.time === a.time) return 0;
    return a.x + ((t - a.time) / (b.time - a.time)) * (b.x - a.x);
  }

  function layout() {
    stageH = stage!.clientHeight || stageH;
    const available = Math.max(0, stageH - TOP_PAD);
    const riseStart = parseDate(RISE_START);
    const lastTime = ticks[ticks.length - 1]?.time ?? riseStart + 1;

    let ref: HTMLElement = stage!;
    let clampW = stage!.clientWidth;
    if (dragMode && combTrack) {
      clampW = Math.max(stage!.clientWidth, yearSpan * MOBILE_YEAR_PX);
      combTrack.style.width = `${clampW}px`;
      combTrack.style.paddingLeft = `${EDGE_PAD}px`;
      combTrack.style.paddingRight = `${EDGE_PAD}px`;
      ref = combTrack;
    }
    const srect = ref.getBoundingClientRect();

    for (const t of ticks) {
      const r = t.el.getBoundingClientRect();
      t.x = r.left + r.width / 2 - srect.left;
      // rest shape: flat until RISE_START, then a hockey-stick climb toward the domain end
      if (t.time <= riseStart) {
        t.restPx = baseBarPx;
      } else {
        const p = clamp(
          0,
          1,
          (t.time - riseStart) / Math.max(1, lastTime - riseStart),
        );
        t.restPx =
          baseBarPx + Math.pow(p, RISE_EXP) * (REST_TOP_FRAC * available - baseBarPx);
      }
      t.activePx = t.milestone
        ? (MAGNITUDE[t.milestone.magnitude] ?? MAGNITUDE.small!) * available
        : t.restPx + FLUFF_BOOST;
      // on the rising tail a milestone's magnitude height can fall below its rest height — never let hover shrink it
      if (t.activePx < t.restPx) t.activePx = t.restPx + FLUFF_BOOST;
      if (PEEK_AT_REST && t.milestone) {
        t.restPx += PEEK_FRACTION * (t.activePx - t.restPx);
      }
      if (LAUNCH_COLOR) {
        const isPeak =
          !!t.milestone &&
          (t.milestone.magnitude === "peak" || t.milestone.highlight);
        t.baseColor = isPeak
          ? PEAK_COLOR
          : t.time >= launchTime
            ? POST_LAUNCH_COLOR
            : PRE_LAUNCH_COLOR;
        t.bar.style.backgroundColor = t.baseColor;
      }
      if (t.pill) {
        // pill.bottom is absolute inside the bar's scaleY box — must be set in px, not %, or scaleY moves it
        t.pill.style.top = "auto";
        t.pill.style.bottom = `${t.activePx + PILL_GAP}px`;
        t.pill.style.right = "auto";
        if (dragMode) {
          t.pill.style.left = `${-PILL_LEAD}px`;
        } else {
          t.pill.style.left = "0px";
          const pw = t.pill.offsetWidth;
          const left = clamp(
            PILL_EDGE,
            Math.max(PILL_EDGE, clampW - pw - PILL_EDGE),
            t.x - PILL_LEAD,
          );
          t.pill.style.left = `${left - t.x}px`;
        }
      }
    }

    for (const p of phases) {
      const x0 = xForTime(p.start);
      const x1 = xForTime(p.end);
      p.x0 = x0;
      p.x1 = x1;
      p.el.style.left = `${x0}px`;
      if (p.lineW) p.lineW.style.width = `${Math.max(0, x1 - x0)}px`;
    }

    applyRest();
    if (dragMode && combTrack && inView) {
      offset = clampOffset(offset);
      applyOffset();
      paintMarker();
    }
  }

  function setBar(t: Tick, px: number, active = false) {
    const sy = px / baseBarPx;
    t.bar.style.transform =
      active && t.milestone
        ? `scaleY(${sy}) scaleX(${MILESTONE_THICK})`
        : `scaleY(${sy})`;
  }

  function applyRest() {
    for (let i = 0; i < ticks.length; i++) {
      const t = ticks[i]!;
      const px = reduced && t.milestone ? t.activePx : t.restPx;
      setBar(t, px, i === activeIndex);
      t.dirty = false;
    }
  }

  function showPill(pill: HTMLElement) {
    gsap.killTweensOf(pill);
    if (reduced) {
      gsap.set(pill, { autoAlpha: 1, y: 0, scale: 1 });
      return;
    }
    gsap.fromTo(
      pill,
      { autoAlpha: 0, y: PILL_RISE },
      {
        autoAlpha: 1,
        y: 0,
        duration: PILL_IN,
        ease: PILL_EASE_IN,
        overwrite: "auto",
      },
    );
  }

  function hidePill(pill: HTMLElement) {
    gsap.killTweensOf(pill);
    if (reduced) {
      gsap.set(pill, { autoAlpha: 0 });
      return;
    }
    gsap.to(pill, {
      autoAlpha: 0,
      y: PILL_RISE * 0.5,
      duration: PILL_OUT,
      ease: PILL_EASE_OUT,
      overwrite: "auto",
    });
  }

  function revealPill(index: number) {
    if (index === activeIndex) return;
    const prev = ticks[activeIndex];
    const next = ticks[index];
    if (prev?.pill) hidePill(prev.pill);
    if (prev?.milestone) setBarColor(prev, prev.baseColor || DEFAULT_BAR_COLOR);
    if (next?.pill) showPill(next.pill);
    if (next?.milestone) {
      setBarColor(next, ACTIVE_COLOR);
      if (dragMode) {
        const now = performance.now();
        if (now - lastVibrate > HAPTIC_GAP) {
          navigator.vibrate?.(HAPTIC_MS);
          lastVibrate = now;
        }
      }
    }
    activeIndex = index;
  }

  function paint(sigma = FALLOFF) {
    const cutoff = sigma * CULL_SIGMAS;
    let nearestMs = -1;
    let nearestDist = Infinity;

    for (let i = 0; i < ticks.length; i++) {
      const t = ticks[i]!;
      const d = t.x - focus;
      const ad = d < 0 ? -d : d;

      if (ad > cutoff) {
        if (t.dirty) {
          setBar(t, t.restPx, false);
          t.dirty = false;
        }
        continue;
      }

      const bump = strength * Math.exp(-(d * d) / (2 * sigma * sigma));
      const px = t.restPx + bump * (t.activePx - t.restPx);
      setBar(t, px, i === activeIndex);
      t.dirty = true;

      if (t.milestone && ad < nearestDist) {
        nearestDist = ad;
        nearestMs = i;
      }
    }

    revealPill(
      lockedIndex >= 0
        ? lockedIndex
        : (engaged || intro || (dragMode && strength > REVEAL_STRENGTH)) &&
            nearestMs >= 0 &&
            nearestDist <= REVEAL_RADIUS
          ? nearestMs
          : -1,
    );

    updatePhase();
  }

  function magnetize(target: number): number {
    if (!MAGNETISM || !engaged || lockedIndex >= 0) return target;
    let mx = 0;
    let md = Infinity;
    for (let i = 0; i < ticks.length; i++) {
      if (!ticks[i]!.milestone) continue;
      const d = Math.abs(ticks[i]!.x - target);
      if (d < md) {
        md = d;
        mx = ticks[i]!.x;
      }
    }
    if (!Number.isFinite(md)) return target;
    const pull =
      MAGNET_PULL * Math.exp(-(md * md) / (2 * MAGNET_RADIUS * MAGNET_RADIUS));
    return target + (mx - target) * pull;
  }

  function tick() {
    if (!inView || idle || intro) return;
    if (lockedIndex >= 0) targetFocus = ticks[lockedIndex]!.x;

    const rawSpeed = Math.abs(targetFocus - prevTarget);
    prevTarget = targetFocus;
    speed += (rawSpeed - speed) * VEL_SMOOTH;
    const velBoost = VELOCITY
      ? 1 + Math.min(1, speed / VEL_REF) * (VEL_MAX - 1)
      : 1;

    focus += (magnetize(targetFocus) - focus) * FOCUS_LERP;
    strength +=
      ((engaged ? 1 : 0) - strength) * (engaged ? WAVE_RISE : WAVE_FALL);
    paint(FALLOFF * velBoost);

    if (!engaged && strength < 0.001) {
      strength = 0;
      applyRest();
      idle = true;
      maybeAutoLoop();
    }
  }

  // smoothstep avoids the corner that a clamped-sine envelope had at the swell-in/hold join
  function introEnvelope(p: number): number {
    const smooth = (u: number) => u * u * (3 - 2 * u);
    if (p < INTRO_RAMP) return smooth(p / INTRO_RAMP);
    if (p > 1 - INTRO_RAMP) return smooth((1 - p) / INTRO_RAMP);
    return 1;
  }

  function startSweep() {
    if (reduced || ticks.length === 0 || introTween) return;
    const loop = AUTO_LOOP && !hasFinePointer;
    if (!loop && (!INTRO_SWEEP || introPlayed)) return;
    introPlayed = true;
    intro = true;
    const startX = -FALLOFF;
    const endX = stage!.clientWidth + FALLOFF;
    const proxy = { p: 0 };
    introTween = gsap.to(proxy, {
      p: 1,
      duration: INTRO_DURATION,
      delay: INTRO_DELAY,
      ease: INTRO_EASE,
      repeat: loop ? -1 : 0,
      repeatDelay: loop ? AUTO_LOOP_GAP : 0,
      onUpdate: () => {
        focus = startX + (endX - startX) * proxy.p;
        strength = introEnvelope(proxy.p);
        paint();
      },
      onComplete: () => {
        intro = false;
        introTween = null;
        strength = 0;
        revealPill(-1);
        applyRest();
      },
    });
  }

  function stopIntro() {
    if (!introTween) return;
    introTween.kill();
    introTween = null;
    intro = false;
  }

  function maybeAutoLoop() {
    if (AUTO_LOOP && !hasFinePointer && inView && lockedIndex < 0) startSweep();
  }

  function lock(i: number) {
    if (!TAP_LOCK || !ticks[i]?.milestone) return;
    lockedIndex = i;
    if (reduced) {
      revealPill(i);
      return;
    }
    engage(ticks[i]!.x);
  }

  function unlock() {
    if (lockedIndex < 0) return;
    lockedIndex = -1;
    if (reduced) {
      revealPill(-1);
      return;
    }
    engaged = false;
  }

  // snap focus on (re)entry — without it `focus` lerps from its parked position and visibly sweeps across the comb
  function engage(at: number) {
    stopIntro();
    targetFocus = at;
    if (!engaged) {
      focus = at;
      prevTarget = at; // prevent a velocity spike on entry
      speed = 0;
    }
    engaged = true;
    idle = false;
  }
  function disengage() {
    if (lockedIndex >= 0) return;
    engaged = false;
  }

  function onPointerMove(e: PointerEvent) {
    const rect = stage!.getBoundingClientRect();
    engage(clamp(0, rect.width, e.clientX - rect.left));
  }
  function onPointerLeave() {
    disengage();
  }

  function nearestMilestone(x: number): { i: number; dist: number } {
    let i = -1;
    let dist = Infinity;
    for (let k = 0; k < ticks.length; k++) {
      if (!ticks[k]!.milestone) continue;
      const d = Math.abs(ticks[k]!.x - x);
      if (d < dist) {
        dist = d;
        i = k;
      }
    }
    return { i, dist };
  }

  function onClick(e: MouseEvent) {
    if (!TAP_LOCK || hasFinePointer) return;
    const rect = stage!.getBoundingClientRect();
    const { i, dist } = nearestMilestone(
      clamp(0, rect.width, e.clientX - rect.left),
    );
    if (i < 0 || dist > LOCK_RADIUS) unlock();
    else if (lockedIndex === i) unlock();
    else lock(i);
  }

  function paintMarker() {
    focus = markerX() - offset;
    paint();
  }

  function bloom() {
    bloomTween?.kill();
    const o = { s: strength };
    bloomTween = gsap.to(o, {
      s: 1,
      duration: BLOOM_DUR,
      delay: BLOOM_DELAY,
      ease: "power2.out",
      onUpdate: () => {
        strength = o.s;
        paintMarker();
      },
      onComplete: () => {
        strength = 1;
        paintMarker();
      },
    });
  }

  function buildMarquee() {
    combTrack = document.createElement("div");
    combTrack.setAttribute("growth-timeline", "comb-track");
    Object.assign(combTrack.style, {
      position: "relative",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      height: "100%",
      // without flex-shrink:0 the stage (also a flex row) collapses the strip back to viewport width
      flexGrow: "0",
      flexShrink: "0",
      willChange: "transform",
    });
    ticks.forEach((t) => combTrack!.appendChild(t.el));
    phases.forEach((p) => combTrack!.appendChild(p.el));
    stage!.appendChild(combTrack);
    setCombX = gsap.quickSetter(combTrack, "x", "px") as (v: number) => void;

    markerEl = document.createElement("div");
    Object.assign(markerEl.style, {
      position: "absolute",
      left: `${MARKER_FRAC * 100}%`,
      top: "0",
      bottom: "0",
      width: "1px",
      transform: "translateX(-50%)",
      backgroundColor: DEFAULT_BAR_COLOR,
      opacity: "0.18",
      pointerEvents: "none",
    });
    stage!.insertBefore(markerEl, combTrack);
  }

  const markerX = () => stage!.clientWidth * MARKER_FRAC;
  const offsetFor = (i: number) => markerX() - ticks[i]!.x;

  function offsetBounds(): { lo: number; hi: number } {
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = 0; i < ticks.length; i++) {
      if (!ticks[i]!.milestone) continue;
      const f = offsetFor(i);
      if (f < lo) lo = f;
      if (f > hi) hi = f;
    }
    return lo > hi ? { lo: offset, hi: offset } : { lo, hi };
  }
  function clampOffset(o: number): number {
    const { lo, hi } = offsetBounds();
    return Math.min(hi, Math.max(lo, o));
  }
  function rubberband(o: number): number {
    const { lo, hi } = offsetBounds();
    if (o > hi) return hi + (o - hi) * OVERSCROLL;
    if (o < lo) return lo + (o - lo) * OVERSCROLL;
    return o;
  }

  const applyOffset = () => setCombX?.(offset);

  function snapOffset(o: number): number {
    const m = markerX();
    let msIdx = -1;
    let msX = 0;
    let msD = Infinity;
    let tickX = 0;
    let tickD = Infinity;
    for (let i = 0; i < ticks.length; i++) {
      const d = Math.abs(ticks[i]!.x + o - m);
      if (d < tickD) {
        tickD = d;
        tickX = ticks[i]!.x;
      }
      if (ticks[i]!.milestone && d < msD) {
        msD = d;
        msX = ticks[i]!.x;
        msIdx = i;
      }
    }
    // cap magnet radius at half the gap to the nearest neighbour so adjacent milestones don't fight over a release
    let mag = MS_MAGNET;
    if (msIdx >= 0) {
      let gap = Infinity;
      for (let j = 0; j < ticks.length; j++) {
        if (j === msIdx || !ticks[j]!.milestone) continue;
        gap = Math.min(gap, Math.abs(ticks[j]!.x - msX));
      }
      if (Number.isFinite(gap)) mag = Math.min(MS_MAGNET, gap * 0.5);
    }
    const targetX = msIdx >= 0 && msD <= mag ? msX : tickX;
    return clampOffset(m - targetX);
  }

  function glideTo(target: number) {
    const dist = Math.abs(target - offset);
    if (dist < 0.5) {
      offset = target;
      applyOffset();
      bloom();
      return;
    }
    const dur = clamp(INERTIA_MIN, INERTIA_MAX, dist / INERTIA_SPEED);
    gsap.to(combTrack!, {
      x: target,
      duration: dur,
      ease: "power3.out",
      onUpdate: () => {
        offset = gsap.getProperty(combTrack!, "x") as number;
        paintMarker();
      },
      onComplete: () => {
        offset = target;
        bloom();
      },
    });
  }

  function nearestTickIndex(): number {
    const m = markerX();
    let best = -1;
    let bd = Infinity;
    for (let i = 0; i < ticks.length; i++) {
      const d = Math.abs(ticks[i]!.x + offset - m);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    return best;
  }

  function magneticSnap(target: number) {
    const dist = Math.abs(target - offset);
    if (dist < 0.5) {
      offset = target;
      applyOffset();
      bloom();
      return;
    }
    gsap.to(combTrack!, {
      x: target,
      duration: SNAP_DUR,
      ease: SNAP_EASE,
      onUpdate: () => {
        offset = gsap.getProperty(combTrack!, "x") as number;
        paintMarker();
      },
      onComplete: () => {
        offset = target;
        bloom();
      },
    });
  }

  function releaseTo(projected: number) {
    const coastDist = Math.abs(projected - offset);
    if (coastDist < 2) {
      magneticSnap(snapOffset(offset));
      return;
    }
    const dur = clamp(INERTIA_MIN, INERTIA_MAX, coastDist / INERTIA_SPEED);
    gsap.to(combTrack!, {
      x: projected,
      duration: dur,
      ease: "power2.out",
      onUpdate: () => {
        offset = gsap.getProperty(combTrack!, "x") as number;
        paintMarker();
      },
      onComplete: () => magneticSnap(snapOffset(projected)),
    });
  }

  function tapSeek(clientX: number) {
    const rect = stage!.getBoundingClientRect();
    const local = clientX - rect.left - offset;
    const { i } = nearestMilestone(local);
    if (i >= 0) glideTo(clampOffset(offsetFor(i)));
  }

  function onDragStart(e: PointerEvent) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    pending = true;
    dragging = false;
    pointerId = e.pointerId;
    downX = e.clientX;
    downY = e.clientY;
    dragStartX = e.clientX;
    dragStartOffset = offset;
    velocity = 0;
    lastMoveX = e.clientX;
    lastMoveTime = performance.now();
    gsap.killTweensOf(combTrack!);
    bloomTween?.kill();
    lastTickIndex = nearestTickIndex();
  }
  function onDragMove(e: PointerEvent) {
    if (pending) {
      const dx = e.clientX - downX;
      const dy = e.clientY - downY;
      if (Math.abs(dx) >= DRAG_THRESH && Math.abs(dx) > Math.abs(dy)) {
        pending = false;
        dragging = true;
        try {
          stage!.setPointerCapture(pointerId);
        } catch {
          /* not all pointers can be captured */
        }
        dragStartX = e.clientX;
        dragStartOffset = offset;
        lastMoveX = e.clientX;
        lastMoveTime = performance.now();
      } else if (Math.abs(dy) >= DRAG_THRESH) {
        pending = false;
      }
      return;
    }
    if (!dragging) return;
    const now = performance.now();
    const dt = now - lastMoveTime;
    if (dt > 0) {
      const v = (e.clientX - lastMoveX) / dt;
      velocity = velocity * 0.6 + v * 0.4;
    }
    lastMoveX = e.clientX;
    lastMoveTime = now;
    offset = rubberband(dragStartOffset + (e.clientX - dragStartX));
    applyOffset();
    const ti = nearestTickIndex();
    if (ti !== lastTickIndex) {
      lastTickIndex = ti;
      const t = performance.now();
      if (t - lastVibrate > HAPTIC_GAP) {
        navigator.vibrate?.(DETENT_HAPTIC);
        lastVibrate = t;
      }
    }
    // wave flattens at speed; you only feel it ease off — not mid-swipe
    const target = clamp(0, 1, 1 - Math.abs(velocity) / SPEED_FULL);
    strength += (target - strength) * STRENGTH_EASE;
    paintMarker();
  }
  function onDragEnd(e: PointerEvent) {
    if (pending) {
      pending = false;
      const moved = Math.abs(e.clientX - downX) + Math.abs(e.clientY - downY);
      if (moved <= TAP_MAX) tapSeek(e.clientX);
      return;
    }
    if (!dragging) return;
    dragging = false;
    if (performance.now() - lastMoveTime > STALE_MS) velocity = 0;
    const projected = clampOffset(offset + velocity * INERTIA_MS);
    releaseTo(projected);
  }
  function onDragCancel() {
    pending = false;
    if (!dragging) return;
    dragging = false;
    glideTo(snapOffset(clampOffset(offset)));
  }
  // prevent vertical page scroll while a horizontal drag is committed — touch-action: pan-y alone allows diagonal drags to scroll
  function onTouchMove(e: TouchEvent) {
    if (dragging && e.cancelable) e.preventDefault();
  }

  const milestoneIndices = () =>
    ticks.map((t, i) => (t.milestone ? i : -1)).filter((i) => i >= 0);

  function onFocusIn(e: FocusEvent) {
    const i = ticks.findIndex((t) => t.el === e.target);
    if (i < 0) return;
    if (dragMode) {
      const target = clampOffset(offsetFor(i));
      gsap.to(combTrack!, {
        x: target,
        duration: INERTIA_MIN,
        ease: "power3.out",
        onUpdate: () => {
          offset = gsap.getProperty(combTrack!, "x") as number;
          paintMarker();
        },
        onComplete: () => {
          offset = target;
          paintMarker();
        },
      });
      return;
    }
    engage(ticks[i]!.x);
    if (reduced) revealPill(i);
  }
  function onFocusOut() {
    if (dragMode) return;
    disengage();
    if (reduced) revealPill(-1);
  }
  function onKeyDown(e: KeyboardEvent) {
    const ms = milestoneIndices();
    if (ms.length === 0) return;
    if ((e.key === "Enter" || e.key === " ") && !hasFinePointer && !dragMode) {
      const i = ticks.findIndex((t) => t.el === e.target);
      if (i >= 0 && ticks[i]!.milestone) {
        e.preventDefault();
        if (lockedIndex === i) unlock();
        else lock(i);
      }
      return;
    }
    const here = ms.indexOf(activeIndex);
    const focusAt = (idx: number) => {
      e.preventDefault();
      ticks[idx]?.el.focus();
    };
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      focusAt(ms[Math.min(ms.length - 1, here + 1)] ?? ms[0]!);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      focusAt(ms[Math.max(0, here - 1)] ?? ms[0]!);
    } else if (e.key === "Home") {
      focusAt(ms[0]!);
    } else if (e.key === "End") {
      focusAt(ms[ms.length - 1]!);
    }
  }

  function setupA11y() {
    stage!.setAttribute("role", "group");
    stage!.setAttribute("aria-roledescription", "timeline");
    stage!.setAttribute("aria-label", A11Y_LABEL);
    const hintId = `gt-hint-${Math.floor(Math.random() * 1e9)}`;
    hintEl = document.createElement("p");
    hintEl.id = hintId;
    hintEl.textContent = A11Y_HINT;
    Object.assign(hintEl.style, SR_ONLY);
    element.appendChild(hintEl);
    stage!.setAttribute("aria-describedby", hintId);
  }

  function applyView(isIn: boolean) {
    inView = isIn;
    if (dragMode) {
      if (isIn) {
        let fi = -1;
        for (let k = 0; k < ticks.length; k++) {
          if (ticks[k]!.milestone) {
            fi = k;
            break;
          }
        }
        if (fi >= 0) {
          offset = clampOffset(offsetFor(fi));
          applyOffset();
          strength = 0;
          bloom();
        }
      } else {
        bloomTween?.kill();
        strength = 0;
        revealPill(-1);
        applyRest();
      }
      return;
    }
    if (isIn) {
      startSweep();
    } else {
      stopIntro();
      lockedIndex = -1;
      engaged = false;
      strength = 0;
      focus = targetFocus;
      revealPill(-1);
      applyRest();
      updatePhase();
      idle = true;
    }
  }

  function resetState() {
    targetFocus = 0;
    focus = 0;
    strength = 0;
    engaged = false;
    idle = true;
    activeIndex = -1;
    activePhase = -1;
    intro = false;
    introPlayed = false;
    lockedIndex = -1;
    offset = 0;
    prevTarget = 0;
    speed = 0;
    dragging = false;
    pending = false;
    pointerId = -1;
  }

  // Build the whole module for the CURRENT mode. Re-runnable: deactivate()
  // tears the previous mode down first, so a resize across the breakpoint
  // rebuilds desktop⇄mobile cleanly.
  const activate = () => {
    resetState();
    // Claim our own pointer area from the host slider's drag: on touch the whole
    // component is the drag-scrub zone (element); on desktop only the hover-scrub
    // stage is, so the rest of the slide stays swipeable. Stage points, not grabs.
    noDragTarget = dragMode ? element : stage;
    noDragTarget.setAttribute("data-no-drag", "");
    if (!dragMode) stage.style.cursor = "default";
    stage.style.position = "relative";
    stage.style.touchAction = "pan-y";
    if (!dragMode) {
      stage.style.justifyContent = "space-between";
      stage.style.paddingLeft = `${EDGE_PAD}px`;
      stage.style.paddingRight = `${EDGE_PAD}px`;
    }

    build();
    if (dragMode) buildMarquee();
    layout();
    setupA11y();

    if (!reduced && !dragMode) stopRaf = Raf.add(tick);

    if (dragMode) {
      // Clip the wide comb strip to the stage horizontally (bleed vertically so
      // pills/labels show) so it can't spill into the neighbouring slides.
      stage.style.clipPath = "inset(-100% 0px -100% 0px)";
      element.style.touchAction = "pan-y";
      element.style.userSelect = "none";
      element.style.webkitUserSelect = "none";
      element.addEventListener("pointerdown", onDragStart);
      element.addEventListener("pointermove", onDragMove);
      element.addEventListener("pointerup", onDragEnd);
      element.addEventListener("pointercancel", onDragCancel);
      element.addEventListener("touchmove", onTouchMove, { passive: false });
    } else {
      stage.addEventListener("pointermove", onPointerMove);
      stage.addEventListener("pointerleave", onPointerLeave);
      stage.addEventListener("click", onClick);
    }
    stage.addEventListener("focusin", onFocusIn);
    stage.addEventListener("focusout", onFocusOut);
    stage.addEventListener("keydown", onKeyDown);

    // Already on-screen (e.g. a resize-driven re-init) — onView won't re-fire,
    // so apply the in-view setup for the new mode now.
    if (inView) applyView(true);
  };

  const deactivate = () => {
    stopRaf();
    stopRaf = () => {};
    stopIntro();
    bloomTween?.kill();
    stage.removeEventListener("pointermove", onPointerMove);
    stage.removeEventListener("pointerleave", onPointerLeave);
    stage.removeEventListener("click", onClick);
    element.removeEventListener("pointerdown", onDragStart);
    element.removeEventListener("pointermove", onDragMove);
    element.removeEventListener("pointerup", onDragEnd);
    element.removeEventListener("pointercancel", onDragCancel);
    element.removeEventListener("touchmove", onTouchMove);
    stage.removeEventListener("focusin", onFocusIn);
    stage.removeEventListener("focusout", onFocusOut);
    stage.removeEventListener("keydown", onKeyDown);
    gsap.killTweensOf(
      ticks.map((t) => t.pill).filter(Boolean) as HTMLElement[],
    );
    gsap.killTweensOf(ticks.map((t) => t.bar));
    ticks.forEach((t) => t.el.remove());
    ticks = [];
    // phases are authored elements (not clones) — restore before dropping the strip so they aren't removed with it
    gsap.killTweensOf(phases.map((p) => p.el));
    phases.forEach((p) => {
      p.el.style.left = "";
      p.el.style.opacity = "";
      p.el.classList.remove(PHASE_ACTIVE_CLASS);
      if (p.lineW) p.lineW.style.width = "";
      if (dragMode) stage.appendChild(p.el);
    });
    phases = [];
    gsap.killTweensOf(combTrack ?? []);
    combTrack?.remove();
    combTrack = null;
    markerEl?.remove();
    markerEl = null;
    setCombX = null;
    hintEl?.remove();
    hintEl = null;
    stage.removeAttribute("role");
    stage.removeAttribute("aria-roledescription");
    stage.removeAttribute("aria-label");
    stage.removeAttribute("aria-describedby");
    stage.style.position = "";
    stage.style.justifyContent = "";
    stage.style.paddingLeft = "";
    stage.style.paddingRight = "";
    stage.style.touchAction = "";
    stage.style.clipPath = "";
    stage.style.cursor = "";
    noDragTarget?.removeAttribute("data-no-drag");
    noDragTarget = null;
    element.style.touchAction = "";
    element.style.userSelect = "";
    element.style.webkitUserSelect = "";
  };

  onMount(() => {
    computeMode();
    activate();

    onView(element, {
      rootMargin: OFFSCREEN_MARGIN,
      autoStart: true,
      callback: ({ isIn }) => applyView(isIn),
    });

    // Re-evaluate the mode on resize / pointer swap. If it crossed the desktop⇄
    // mobile boundary, rebuild in the new mode; otherwise just re-measure.
    // deactivate() runs before computeMode() so it tears down the OLD mode.
    const relayout = () => {
      if (prospectiveDrag() !== dragMode) {
        deactivate();
        computeMode();
        activate();
      } else {
        layout();
      }
    };
    const stopResize = Resize.add(relayout);
    fineQuery.addEventListener("change", relayout);

    onDestroy(() => {
      deactivate();
      stopResize();
      fineQuery.removeEventListener("change", relayout);
    });
  });
}
